import { useEffect, useRef } from "react";

export default function TableScrollbar({ tableRef, data, trigger, className = '' }) {
  const thumbRef = useRef(null);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startScrollLeftRef = useRef(0);

  useEffect(() => {
    const table = tableRef.current;
    const thumb = thumbRef.current;
    const parent = table?.parentElement;

    if (!table || !thumb || !parent) return;

    const updateThumb = () => {
      const visible = parent.clientWidth;
      const total = table.scrollWidth;

      if (total <= visible) {
        thumb.style.display = "none";
        return;
      }

      const ratio = visible / total;
      const thumbWidth = Math.max(visible * ratio, 30);

      thumb.style.display = "block";
      thumb.style.width = `${thumbWidth}px`;
      syncThumb();
    };

    const syncThumb = () => {
      const maxScroll = table.scrollWidth - parent.clientWidth;
      const maxThumbX = parent.clientWidth - thumb.offsetWidth;

      const scrollRatio = parent.scrollLeft / maxScroll;
      thumb.style.transform = `translateX(${maxThumbX * scrollRatio}px)`;
    };

    const onScroll = () => syncThumb();

    const onPointerDown = (e) => {
      draggingRef.current = true;
      startXRef.current = e.clientX;
      startScrollLeftRef.current = parent.scrollLeft;
      thumb.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e) => {
      if (!draggingRef.current) return;

      const dx = e.clientX - startXRef.current;
      const maxThumbX = parent.clientWidth - thumb.offsetWidth;
      const maxScroll = table.scrollWidth - parent.clientWidth;

      parent.scrollLeft =
        startScrollLeftRef.current +
        (dx / maxThumbX) * maxScroll;
    };

    const onPointerUp = () => {
      draggingRef.current = false;
    };

    parent.addEventListener("scroll", onScroll);
    window.addEventListener("resize", updateThumb);
    thumb.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);

    updateThumb();

    return () => {
      parent.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateThumb);
      thumb.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [data, trigger]);

  const onTrackClick = (e) => {
    const table = tableRef.current;
    const parent = table?.parentElement;
    const thumb = thumbRef.current;

    if (!table || !parent || !thumb) return;
    if (e.target !== e.currentTarget) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;

    const thumbCenter = thumb.offsetWidth / 2;
    const maxThumbX = parent.clientWidth - thumb.offsetWidth;
    const maxScroll = table.scrollWidth - parent.clientWidth;

    const thumbX = Math.min(
      Math.max(clickX - thumbCenter, 0),
      maxThumbX
    );

    parent.scrollLeft = (thumbX / maxThumbX) * maxScroll;
  };

  return (
    <div className={`sticky-bottom table-scrollbar ${className}`}>
      <div className="scrollbar" onClick={onTrackClick} data-testid="table-scrollbar">
        <div ref={thumbRef} className="thumb" />
      </div>
    </div>
  );
}
